
import React from 'react';
import { Priority } from '../types';

/**
 * Props for the PriorityIcon component.
 */
interface PriorityIconProps {
  priority: Priority;
}

/**
 * A component that displays a specific icon and color based on the task's priority level.
 * @param {PriorityIconProps} props - The props for the component.
 * @returns {React.ReactElement} - The rendered priority icon.
 */
const PriorityIcon: React.FC<PriorityIconProps> = ({ priority }) => {
  // Fix: Replaced `JSX.Element` with `React.ReactNode` to resolve the "Cannot find namespace 'JSX'" error.
  // A mapping from priority enum to its corresponding icon, color, and tooltip.
  const iconData: { [key in Priority]: { icon: React.ReactNode; color: string; tooltip: string } } = {
    [Priority.Critical]: {
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" viewBox="0 0 20 20" fill="currentColor">
          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
        </svg>
      ),
      color: 'text-red-600',
      tooltip: 'Critical Priority'
    },
    [Priority.High]: {
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
        </svg>
      ),
      color: 'text-orange-500',
      tooltip: 'High Priority'
    },
    [Priority.Medium]: {
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
      color: 'text-yellow-500',
      tooltip: 'Medium Priority'
    },
    [Priority.Low]: {
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
      color: 'text-green-500',
      tooltip: 'Low Priority'
    },
  };

  // Select the appropriate icon data, defaulting to Medium if the priority is somehow invalid.
  const { icon, color, tooltip } = iconData[priority] || iconData[Priority.Medium];

  return (
    <div className={`relative group flex-shrink-0 ${color}`} title={tooltip}>
      {icon}
    </div>
  );
};

export default PriorityIcon;
